import { apiClient } from './apiClient';
import { sessionStorageUtils } from './sessionStorage';

// Token interceptor for handling automatic logout
class TokenInterceptor {
  private static instance: TokenInterceptor;
  private logoutCallback?: () => void;
  private dispatch?: any;

  private constructor() {
    // Set up the token expiration callback
    apiClient.setTokenExpiredCallback(() => {
      this.handleTokenExpiration();
    });
  }

  static getInstance(): TokenInterceptor {
    if (!TokenInterceptor.instance) {
      TokenInterceptor.instance = new TokenInterceptor();
    }
    return TokenInterceptor.instance;
  }

  // Set logout callback (should be called from auth slice)
  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  // Set Redux dispatch for auto logout
  setDispatch(dispatch: any) {
    this.dispatch = dispatch;
  }

  // Handle token expiration
  private handleTokenExpiration() {
    
    // Clear session storage
    sessionStorageUtils.removeToken();
    
    // Trigger Redux auto logout if dispatch is available
    if (this.dispatch) {
      // Import autoLogout action dynamically to avoid circular dependency
      import('../../store/slices/authSlice').then(({ autoLogout }) => {
        this.dispatch(autoLogout());
      });
    }
    
    // Trigger logout callback if set
    if (this.logoutCallback) {
      this.logoutCallback();
    }
    
    // Redirect to login page
    window.location.href = '/login';
  }

  // Check if token exists and is valid
  isTokenValid(): boolean {
    const token = sessionStorageUtils.getToken();
    return !!token;
  }

  // Get current token
  getToken(): string | null {
    return sessionStorageUtils.getToken();
  }

  // Clear token and logout
  clearToken() {
    sessionStorageUtils.removeToken();
    if (this.logoutCallback) {
      this.logoutCallback();
    }
  }

  // Start periodic token validation
  startTokenValidation(intervalMs: number = 60000) { // Default 1 minute
    setInterval(() => {
      if (this.isTokenValid()) {
        // Validate token with backend
        this.validateTokenWithBackend();
      }
    }, intervalMs);
  }

  // Validate token with backend
  private async validateTokenWithBackend() {
    try {
      const token = this.getToken();
      if (!token) return;

      // Use the auth service to validate token
      const { authService } = await import('../../services/authService');
      const response = await authService.validateToken(token);
      
      if (!response.valid) {
        this.handleTokenExpiration();
      }
    } catch (error) {
      this.handleTokenExpiration();
    }
  }
}

// Export singleton instance
export const tokenInterceptor = TokenInterceptor.getInstance();
